import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";
import { DEFAULT_CATEGORIES } from "@/lib/api";

export default function PrayerFormDialog({ open, onOpenChange, onSubmit, initial, availableCategories }) {
  const [title, setTitle] = useState("");
  const [forWhom, setForWhom] = useState("");
  const [details, setDetails] = useState("");
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setForWhom(initial?.for_whom || "");
      setDetails(initial?.details || "");
      setCats(initial?.categories || []);
      setNewCat("");
    }
  }, [open, initial]);

  const toggleCat = (c) => {
    setCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const addCat = () => {
    const v = newCat.trim();
    if (!v) return;
    if (!cats.includes(v)) setCats([...cats, v]);
    setNewCat("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        for_whom: forWhom.trim(),
        details: details.trim(),
        categories: cats,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const suggestions = Array.from(new Set([...(availableCategories || []), ...DEFAULT_CATEGORIES])).slice(0, 12);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-white border-[#D4C7E1] rounded-2xl p-0 gap-0 max-h-[92dvh] w-[calc(100%-1.5rem)] sm:w-full flex flex-col overflow-hidden"
        data-testid="prayer-form-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0 text-left">
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
            {initial ? "Edit prayer" : "New prayer"}
          </DialogTitle>
          <DialogDescription className="text-[#75737A]">
            Write what's on your heart. Nothing is too small.
          </DialogDescription>
        </DialogHeader>

        <form
          id="prayer-form"
          onSubmit={handleSave}
          className="flex-1 min-h-0 overflow-y-auto px-6 pt-2 pb-4 space-y-5"
        >
          <div className="space-y-1.5">
            <Label htmlFor="prayer-title" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
              Title
            </Label>
            <Input
              id="prayer-title"
              data-testid="prayer-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Peace for Mom's surgery"
              maxLength={200}
              autoFocus
              required
              className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prayer-for" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
              For (person or thing)
            </Label>
            <Input
              id="prayer-for"
              data-testid="prayer-for-input"
              value={forWhom}
              onChange={(e) => setForWhom(e.target.value)}
              placeholder="e.g. Mom"
              maxLength={200}
              className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prayer-details" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
              Details
            </Label>
            <Textarea
              id="prayer-details"
              data-testid="prayer-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Anything on your heart…"
              rows={4}
              maxLength={4000}
              className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#75737A]">Categories</Label>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((c) => {
                const active = cats.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCat(c)}
                    className={`chip ${active ? "chip-active" : ""}`}
                    data-testid={`cat-toggle-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCat();
                  }
                }}
                placeholder="Add a new category"
                maxLength={30}
                data-testid="new-category-input"
                className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-10 flex-1"
              />
              <Button
                type="button"
                onClick={addCat}
                variant="outline"
                className="rounded-xl border-[#D4C7E1] h-10"
                data-testid="add-category-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {cats.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2">
                {cats.map((c) => (
                  <span key={c} className="chip chip-active">
                    {c}
                    <button
                      type="button"
                      onClick={() => toggleCat(c)}
                      className="ml-1 opacity-80 hover:opacity-100"
                      aria-label={`Remove ${c}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

        </form>

        <DialogFooter className="gap-2 px-6 py-4 border-t border-[#EFE7F5] bg-white shrink-0 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-[#D4C7E1]"
            onClick={() => onOpenChange(false)}
            data-testid="prayer-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="prayer-form"
            className="btn-primary rounded-full px-6"
            disabled={saving || !title.trim()}
            data-testid="prayer-save-btn"
          >
            {saving ? "Saving…" : initial ? "Save changes" : "Add prayer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
